import InternalChatAPI from '../../../api/internalChat';
import * as types from './types';

export const actions = {
  getConversations: async ({ commit }, { page = 1 } = {}) => {
    commit(types.SET_UI_FLAG, { isFetchingConversations: true });
    try {
      const response = await InternalChatAPI.get();
      commit(types.SET_CONVERSATIONS, response.data.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingConversations: false });
    }
  },

  createConversation: async ({ commit }, payload) => {
    commit(types.SET_UI_FLAG, { isCreatingConversation: true });
    try {
      const response = await InternalChatAPI.create(payload);
      commit(types.ADD_CONVERSATION, response.data.data);
      return response.data.data;
    } finally {
      commit(types.SET_UI_FLAG, { isCreatingConversation: false });
    }
  },

  addConversation: ({ commit }, data) => {
    commit(types.ADD_CONVERSATION, data);
  },

  deleteConversation: async ({ commit }, id) => {
    try {
      await InternalChatAPI.delete(id);
      commit(types.REMOVE_CONVERSATION, id);
    } catch (error) {
      // Ignore error
    }
  },

  updateConversation: async ({ commit }, payload) => {
    try {
      let id;
      let data;
      if (payload.payload instanceof FormData) {
        id = payload.id;
        data = payload.payload;
      } else {
        const { id: payloadId, ...rest } = payload;
        id = payloadId;
        data = rest;
      }
      const response = await InternalChatAPI.update(id, data);
      commit(types.UPDATE_CONVERSATION, response.data.data);
      return response.data.data;
    } catch (error) {
      // Ignore error
      throw error;
    }
  },

  updateConversationActivity: (
    { commit, state, rootGetters },
    { conversationId, lastActivityAt, senderId }
  ) => {
    const conversation = state.records.find(c => c.id === conversationId);
    if (!conversation) return;

    const currentUserId = rootGetters.getCurrentUserID;
    const isFromCurrentUser = senderId === currentUserId;

    const updatedConversation = {
      ...conversation,
      last_activity_at: lastActivityAt,
    };

    // Only increment unread count if the message is from someone else
    if (!isFromCurrentUser) {
      updatedConversation.unread_count = (conversation.unread_count || 0) + 1;
    }

    // Bump the conversation to the top by removing and re-adding?
    // Vuex reactivity handles it if we just update it.
    commit(types.UPDATE_CONVERSATION, updatedConversation);
  },

  getMessages: async ({ commit }, { conversationId, page = 1 }) => {
    commit(types.SET_UI_FLAG, { isFetchingMessages: true });
    try {
      const response = await InternalChatAPI.getMessages({
        conversationId,
        page,
      });
      commit(types.SET_MESSAGES, {
        conversationId,
        messages: response.data.data,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingMessages: false });
    }
  },

  sendMessage: async ({ commit }, { conversationId, payload }) => {
    commit(types.SET_UI_FLAG, { isSendingMessage: true });
    try {
      const response = await InternalChatAPI.sendMessage({
        conversationId,
        payload,
      });
      commit(types.ADD_MESSAGE, {
        conversationId,
        message: response.data.data,
      });
      return response.data.data;
    } finally {
      commit(types.SET_UI_FLAG, { isSendingMessage: false });
    }
  },

  addMessage: ({ commit }, data) => {
    commit(types.ADD_MESSAGE, {
      conversationId: data.internal_conversation_id,
      message: data,
    });
  },

  deleteMessage: async ({ commit }, { conversationId, messageId }) => {
    try {
      await InternalChatAPI.deleteMessage({ conversationId, messageId });
      commit(types.REMOVE_MESSAGE, { conversationId, messageId });
    } catch (error) {
      // Ignore error
    }
  },

  removeMessage: ({ commit }, { conversationId, messageId }) => {
    commit(types.REMOVE_MESSAGE, { conversationId, messageId });
  },

  markAsRead: async ({ commit, state }, { conversationId }) => {
    try {
      await InternalChatAPI.markAsRead({ conversationId });
      const conversation = state.records.find(c => c.id === conversationId);
      if (conversation) {
        commit(types.UPDATE_CONVERSATION, {
          ...conversation,
          unread_count: 0,
        });
      }
    } catch (error) {
      // Ignore error
    }
  },

  markAsUnread: async ({ commit, state }, { conversationId }) => {
    try {
      await InternalChatAPI.markAsUnread({ conversationId });
      const conversation = state.records.find(c => c.id === conversationId);
      if (conversation) {
        commit(types.UPDATE_CONVERSATION, {
          ...conversation,
          unread_count: 1,
        });
      }
    } catch (error) {
      // Ignore error
    }
  },

  addParticipants: async ({ commit }, { conversationId, userIds, role }) => {
    try {
      const response = await InternalChatAPI.addParticipants({
        conversationId,
        userIds,
        role,
      });
      return response.data.data;
    } catch (error) {
      // Ignore error
    }
  },

  removeParticipant: async ({ commit }, { conversationId, userId }) => {
    try {
      await InternalChatAPI.removeParticipant({ conversationId, userId });
    } catch (error) {
      // Ignore error
    }
  },

  updateParticipantRole: async (
    { commit },
    { conversationId, userId, role }
  ) => {
    try {
      await InternalChatAPI.updateParticipantRole({
        conversationId,
        userId,
        role,
      });
    } catch (error) {
      // Ignore error
    }
  },

  superviseUser: async ({ commit }, { userId }) => {
    commit(types.SET_UI_FLAG, { isFetchingSupervised: true });
    try {
      const response = await InternalChatAPI.supervise({ userId });
      commit(types.SET_SUPERVISED_CONVERSATIONS, response.data.data);
      commit(types.SET_SUPERVISED_USER, response.data.user);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_UI_FLAG, { isFetchingSupervised: false });
    }
  },

  clearSupervision: ({ commit }) => {
    commit(types.SET_SUPERVISED_CONVERSATIONS, []);
    commit(types.SET_SUPERVISED_USER, null);
  },
};
